import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { Skeleton } from "@libs/rubick";
import { Box, NoSsr } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { NoRowsOverlay } from "./NoRowsOverlay";
import { Button } from "@/components/ui";
import { StatusBadge } from "@/components/ui/Badge";
import { formatDate } from "@/utils/date";
export var ApiKeyStatus;
(function(ApiKeyStatus) {
    ApiKeyStatus["Active"] = "Active";
    ApiKeyStatus["Inactive"] = "Inactive";
    ApiKeyStatus["Disabled"] = "Disabled";
})(ApiKeyStatus || (ApiKeyStatus = {}));
export const ApiKeysGrid = ({ emptyAction, emptyMessage = 'No API keys found. Get started by creating your first API key.', emptyTitle, handleDelete, handleStatusUpdate, heightMode = 'fixed', rows, isLoading, isViewer = false, isUpdating = null, userCustomerId })=>{
    const headerTailwindStyles = 'bg-gray-100 font-bold text-gray-700 text-sm';
    const noRowsMsg = emptyMessage;
    const columns = [
        {
            field: 'name',
            flex: 1,
            headerClassName: headerTailwindStyles,
            headerName: 'API Key Name',
            sortable: false
        },
        {
            field: 'productCode',
            headerClassName: headerTailwindStyles,
            headerName: 'Product',
            sortable: false,
            width: 150,
            renderCell: (params)=>{
                return /*#__PURE__*/ _jsx("span", {
                    className: "text-sm text-gray-700 font-medium",
                    children: params.value || '-'
                });
            }
        },
        {
            field: 'email',
            headerClassName: headerTailwindStyles,
            headerName: 'Owner',
            sortable: false,
            width: 200,
            renderCell: (params)=>{
                return /*#__PURE__*/ _jsx("span", {
                    className: "text-sm text-gray-600",
                    children: params.value || '-'
                });
            }
        },
        {
            field: 'createdDate',
            headerClassName: headerTailwindStyles,
            headerName: 'Created Date',
            sortable: false,
            width: 170,
            renderCell: (params)=>{
                return /*#__PURE__*/ _jsx("div", {
                    className: "",
                    children: formatDate(params.value)
                });
            }
        },
        {
            field: 'expiresAt',
            headerClassName: headerTailwindStyles,
            headerName: 'Expires',
            sortable: false,
            width: 170,
            renderCell: (params)=>{
                const expiresAt = params.row.expiresAt;
                const gracePeriodDays = params.row.gracePeriodDays;
                if (!expiresAt) {
                    return /*#__PURE__*/ _jsx("div", {
                        className: "",
                        children: "-"
                    });
                }
                return /*#__PURE__*/ _jsx("div", {
                    className: "",
                    title: gracePeriodDays ? `Includes ${gracePeriodDays}-day grace period` : undefined,
                    children: formatDate(expiresAt)
                });
            }
        },
        {
            align: 'center',
            cellClassName: 'flex items-center',
            field: 'status',
            headerAlign: 'center',
            headerClassName: headerTailwindStyles,
            headerName: 'Status',
            sortable: false,
            width: 140,
            renderCell: (params)=>{
                return /*#__PURE__*/ _jsx("div", {
                    className: "flex w-full items-center justify-center",
                    children: /*#__PURE__*/ _jsx(StatusBadge, {
                        status: params.value || 'Active'
                    })
                });
            }
        },
        ...!isViewer ? [
            {
                cellClassName: `flex items-center justify-center`,
                align: 'center',
                field: ' ',
                headerName: 'Actions',
                headerAlign: 'center',
                headerClassName: headerTailwindStyles,
                renderCell: (cellData)=>{
                    const isActive = cellData.row.status === 'Active';
                    const isCurrentlyUpdating = isUpdating === cellData.row.id;
                    const canModifyRow = !userCustomerId || cellData.row.customerId === userCustomerId;
                    const contractExpired = cellData.row.contractExpired === true;
                    // Can only enable if contract is not expired; can always disable
                    const canToggleStatus = isActive || !contractExpired;
                    if (!canModifyRow) {
                        return /*#__PURE__*/ _jsx("div", {
                            className: "flex items-center justify-center gap-2 w-full text-gray-400 text-sm",
                            children: "-"
                        });
                    }
                    return /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center justify-center gap-2 w-full",
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                variant: "simple",
                                buttonStyle: "flat",
                                className: `p-2 ${isCurrentlyUpdating || !canToggleStatus ? 'opacity-50' : ''}`,
                                disabled: isCurrentlyUpdating || !canToggleStatus,
                                onClick: (e)=>{
                                    e.stopPropagation();
                                    handleStatusUpdate(cellData.row.id, isActive ? 'disable' : 'enable');
                                },
                                title: !canToggleStatus ? 'Contract expired - cannot enable' : isActive ? 'Disable API Key' : 'Enable API Key',
                                children: isActive ? /*#__PURE__*/ _jsx("svg", {
                                    className: "w-5 h-5 text-yellow-600",
                                    fill: "none",
                                    stroke: "currentColor",
                                    viewBox: "0 0 24 24",
                                    children: /*#__PURE__*/ _jsx("path", {
                                        strokeLinecap: "round",
                                        strokeLinejoin: "round",
                                        strokeWidth: 2,
                                        d: "M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                                    })
                                }) : /*#__PURE__*/ _jsx("svg", {
                                    className: `w-5 h-5 ${contractExpired ? 'text-gray-400' : 'text-green-600'}`,
                                    fill: "none",
                                    stroke: "currentColor",
                                    viewBox: "0 0 24 24",
                                    children: /*#__PURE__*/ _jsx("path", {
                                        strokeLinecap: "round",
                                        strokeLinejoin: "round",
                                        strokeWidth: 2,
                                        d: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                variant: "simple",
                                buttonStyle: "flat",
                                className: "p-2",
                                onClick: (e)=>{
                                    e.stopPropagation();
                                    handleDelete(cellData.row.id);
                                },
                                disabled: isCurrentlyUpdating,
                                title: "Delete API Key",
                                "data-testid": `remove-api-key-${cellData.row.name}`,
                                children: /*#__PURE__*/ _jsx("svg", {
                                    className: "w-5 h-5 text-red-600",
                                    fill: "none",
                                    stroke: "currentColor",
                                    viewBox: "0 0 24 24",
                                    children: /*#__PURE__*/ _jsx("path", {
                                        strokeLinecap: "round",
                                        strokeLinejoin: "round",
                                        strokeWidth: 2,
                                        d: "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    })
                                })
                            })
                        ]
                    });
                },
                sortable: false,
                width: 150
            }
        ] : []
    ];
    const gridSlotProps = {
        toolbar: {
            showQuickFilter: false
        }
    };
    const gridClassOverrides = {
        '& .MuiDataGrid-columnHeader': {
            padding: '0 16px'
        },
        '& .MuiDataGrid-cell': {
            padding: '0 16px'
        },
        '& .MuiDataGrid-row:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
        }
    };
    const gridContainerSx = heightMode === 'fill' ? {
        flex: 1,
        height: '100%',
        minHeight: 400,
        position: 'relative',
        width: '100%'
    } : {
        flex: 1,
        height: 400,
        position: 'relative'
    };
    if (isLoading) {
        return /*#__PURE__*/ _jsx(Box, {
            sx: gridContainerSx,
            children: /*#__PURE__*/ _jsx("div", {
                className: "animate-pulse",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "bg-white shadow overflow-hidden sm:rounded-md",
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "px-4 py-5 sm:p-6",
                        children: [
                            /*#__PURE__*/ _jsx(Skeleton, {
                                className: "h-4 w-1/4 rounded mb-4"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "space-y-3",
                                children: [
                                    ...Array(3)
                                ].map((_, i)=>/*#__PURE__*/ _jsx(Skeleton, {
                                        className: "h-4 rounded"
                                    }, i))
                            })
                        ]
                    })
                })
            })
        });
    }
    return /*#__PURE__*/ _jsx(Box, {
        sx: gridContainerSx,
        children: /*#__PURE__*/ _jsx(NoSsr, {
            children: /*#__PURE__*/ _jsx(DataGrid, {
                columns: columns,
                disableColumnMenu: true,
                hideFooter: true,
                onRowClick: ()=>{
                // Future: navigate to API key details page
                },
                rows: rows,
                slotProps: gridSlotProps,
                slots: {
                    noResultsOverlay: ()=>/*#__PURE__*/ _jsx(NoRowsOverlay, {
                            message: noRowsMsg
                        }),
                    noRowsOverlay: ()=>/*#__PURE__*/ _jsx(NoRowsOverlay, {
                            action: emptyAction,
                            message: noRowsMsg,
                            title: emptyTitle
                        })
                },
                sx: gridClassOverrides,
                loading: isLoading
            })
        })
    });
};
